import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Calendar, Clock, Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Confirmation = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (error) {
      console.error('Error fetching booking:', error);
    }
  };

  if (!booking) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <p className="text-xl">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Success Icon */}
        <div className="text-center mb-12" data-testid="confirmation-success">
          <CheckCircle size={80} className="mx-auto mb-6 text-[var(--accent)]" />
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
            BOOKING SUBMITTED!
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-zinc-600">
            Thank you for booking with STUDIO AT THE THIRD
          </p>
        </div>

        {/* Booking Details */}
        <div className="bg-white border-2 border-black p-8 mb-8" data-testid="booking-details">
          <h2 className="text-2xl font-bold mb-6 pb-4 border-b-2 border-[var(--accent)]" style={{ fontFamily: 'var(--heading-font)' }}>
            Booking Details
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="bg-zinc-100 p-3 rounded-full">
                <Calendar size={24} />
              </div>
              <div>
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Booking ID</p>
                <p className="font-bold text-lg">{booking.id}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-zinc-100 p-3 rounded-full">
                <Calendar size={24} />
              </div>
              <div>
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Date</p>
                <p className="font-bold text-lg">{booking.booking_date}</p>
              </div>
            </div>

            {booking.booking_time && (
              <div className="flex items-start gap-4">
                <div className="bg-zinc-100 p-3 rounded-full">
                  <Clock size={24} />
                </div>
                <div>
                  <p className="text-sm text-zinc-500 uppercase tracking-wide">Time</p>
                  <p className="font-bold text-lg">{booking.booking_time}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-4">
              <div className="bg-zinc-100 p-3 rounded-full">
                <Mail size={24} />
              </div>
              <div>
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Email</p>
                <p className="font-bold text-lg">{booking.customer_email}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-zinc-100 p-3 rounded-full">
                <Phone size={24} />
              </div>
              <div>
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Phone</p>
                <p className="font-bold text-lg">{booking.customer_phone}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-zinc-100 p-3 rounded-full">
                <CheckCircle size={24} />
              </div>
              <div>
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Payment Status</p>
                <p className="font-bold text-lg capitalize">{booking.payment_status}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-[var(--accent-soft)] border border-[var(--accent)] p-8 mb-8">
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
            What's Next?
          </h3>
          <ul className="space-y-3 text-zinc-700">
            <li className="flex items-start gap-3">
              <span className="font-bold text-[var(--accent)]">1.</span>
              <span>Our team will verify your payment within 24 hours</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-bold text-[var(--accent)]">2.</span>
              <span>You will receive a confirmation email once payment is verified</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-bold text-[var(--accent)]">3.</span>
              <span>Show your booking confirmation at the studio on your scheduled date</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <Link to="/" className="block">
            <button className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-8 py-4 text-lg font-bold transition-transform hover:scale-105 active:scale-95" data-testid="back-home-btn">
              BACK TO HOME
            </button>
          </Link>
          <Link to={booking.booking_type === 'event' ? '/events' : '/classes'} className="block">
            <button className="w-full border-2 border-black text-black hover:bg-black hover:text-white rounded-full px-8 py-4 text-lg transition-colors" data-testid="book-another-btn">
              {booking.booking_type === 'event' ? 'BOOK ANOTHER TICKET' : 'BOOK ANOTHER CLASS'}
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;